/*
 * Copied from TRPC:
 * https://github.com/trpc/trpc/blob/96de9f28b1cf62982276fa6e896bb217b07b67b9/packages/server/src/error/TRPCError.ts
 * All other functionality inlined from other files
 *
 * Changes:
 * - Renamed to AppError
 * - Added APP_ERROR_CODE instead of TRPC error codes and numbers
 */
export class AppError extends Error {
  public override readonly cause?: Error;
  public readonly code;

  constructor(opts: {
    message?: string;
    code: APP_ERROR_CODE_KEY;
    cause?: unknown;
  }) {
    const cause = getCauseFromUnknown(opts.cause);
    const message = opts.message ?? cause?.message ?? opts.code;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore https://github.com/tc39/proposal-error-cause
    super(message, { cause });

    this.code = opts.code;
    this.name = 'AppError';

    if (!this.cause) {
      // idk why this is needed, but it is
      this.cause = cause;
    }
  }
}

const APP_ERROR_CODE = {
  BAD_REQUEST: 400,
  INTERNAL_SERVER_ERROR: 500,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_SUPPORTED: 405,
  TIMEOUT: 408,
  CONFLICT: 409,
  PRECONDITION_FAILED: 412,
  PAYLOAD_TOO_LARGE: 413,
  UNPROCESSABLE_CONTENT: 422,
  TOO_MANY_REQUESTS: 429,
  CLIENT_CLOSED_REQUEST: 499,
} as const;

type APP_ERROR_CODE_KEY = keyof typeof APP_ERROR_CODE;

export function getTRPCErrorFromUnknown(cause: unknown): AppError {
  if (cause instanceof AppError) {
    return cause;
  }
  if (cause instanceof Error && cause.name === 'AppError') {
    // https://github.com/trpc/trpc/pull/4848
    return cause as AppError;
  }

  const appError = new AppError({
    code: 'INTERNAL_SERVER_ERROR',
    cause,
  });

  // Inherit stack from error
  if (cause instanceof Error && cause.stack) {
    appError.stack = cause.stack;
  }

  return appError;
}

class UnknownCauseError extends Error {
  [key: string]: unknown;
}

function getCauseFromUnknown(cause: unknown): Error | undefined {
  if (cause instanceof Error) {
    return cause;
  }

  const type = typeof cause;
  if (type === 'undefined' || type === 'function' || cause === null) {
    return undefined;
  }

  // Primitive types just get wrapped in an error
  if (type !== 'object') {
    return new Error(String(cause));
  }

  // If it's an object, we'll create a synthetic error
  if (isObject(cause)) {
    const err = new UnknownCauseError();
    for (const key in cause) {
      err[key] = cause[key];
    }
    return err;
  }

  return undefined;
}

export function isObject(value: unknown): value is Record<string, unknown> {
  // check that value is object
  return !!value && !Array.isArray(value) && typeof value === 'object';
}
